import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApiService } from 'app/shared/apis/api.service';
import { UserModel } from 'app/models/user.model';
import { Observable, map, BehaviorSubject, filter, take, switchMap, of, catchError, shareReplay, timeout } from 'rxjs';
import { environment } from 'environments/environment';
import {
    ENFORCE_FOR_ADMIN_CODE,
    isEnforcedAdminPermissionCode,
} from 'app/core/auth/admin-panel-permissions';

@Injectable({
    providedIn: 'root',
})
export class PermissionService {
    private currentUser$ = new BehaviorSubject<UserModel | null>(null);
    private readonly http = inject(HttpClient);
    /** Garante group/permissions frescos da API (JWT pode estar desatualizado). */
    private readonly permissionsReady$: Observable<boolean>;

    constructor() {
        ApiService.userCurrent.subscribe((user) => {
            this.currentUser$.next(user);
        });

        this.permissionsReady$ = ApiService.userCurrent.pipe(
            filter((user): user is UserModel => !!user?.user_id),
            take(1),
            timeout({ first: 5000, with: () => of(null) }),
            switchMap((user) => {
                if (!user?.user_id) {
                    return of(true);
                }
                return this.http.get<UserModel>(`${environment.apiUrl}/user/${user.user_id}`).pipe(
                    map((fresh) => {
                        if (fresh?.permission_group) {
                            ApiService.userNext({
                                ...user,
                                ...fresh,
                                user_companies: fresh.user_companies ?? user.user_companies,
                                permission_group: fresh.permission_group,
                            } as UserModel);
                        }
                        return true;
                    }),
                    catchError(() => of(true)),
                );
            }),
            shareReplay(1),
        );
        // Dispara o refresh imediatamente
        this.permissionsReady$.subscribe();
    }

    /** Aguarda o refresh de permissões do usuário (ou segue se já concluído). */
    whenReady(): Observable<boolean> {
        return this.permissionsReady$.pipe(filter(Boolean), take(1));
    }

    hasPermission(code: string): boolean {
        return this.checkPermission(this.currentUser$.value, code);
    }

    private groupCodes(user: UserModel | null): string[] {
        return (user?.permission_group?.permissions || []).map((p) => p.code).filter(Boolean);
    }

    private groupHas(user: UserModel | null, code: string): boolean {
        return this.groupCodes(user).includes(code);
    }

    /**
     * Bypass só se is_admin && !ENFORCE_FOR_ADMIN.
     * Com ENFORCE: ADMIN_*_VIEW + CUD clássicos mapeados + COMPANY_COCKPIT_VIEW exigem o group.
     */
    private checkPermission(currentUser: UserModel | null, code: string): boolean {
        if (!currentUser) {
            return false;
        }

        if (currentUser.is_admin) {
            const enforce = this.groupHas(currentUser, ENFORCE_FOR_ADMIN_CODE);
            if (!enforce) {
                return true;
            }
            if (isEnforcedAdminPermissionCode(code)) {
                return this.groupHas(currentUser, code);
            }
            return true;
        }

        return this.groupHas(currentUser, code);
    }

    hasAnyPermission(codes: string[]): boolean {
        const currentUser = this.currentUser$.value;
        if (!currentUser) {
            return false;
        }
        return codes.some((code) => this.checkPermission(currentUser, code));
    }

    hasAllPermissions(codes: string[]): boolean {
        const currentUser = this.currentUser$.value;
        if (!currentUser) {
            return false;
        }
        return codes.every((code) => this.checkPermission(currentUser, code));
    }

    isAdmin(): boolean {
        return this.currentUser$.value?.is_admin || false;
    }

    getCurrentUser(): UserModel | null {
        return this.currentUser$.value;
    }

    hasPermission$(code: string): Observable<boolean> {
        return ApiService.userCurrent.pipe(map((user) => this.checkPermission(user, code)));
    }
}
